import React, { useReducer, useState } from 'react'
import { defaultParams, mapResultKeyToArray } from 'utils/helpers'
import { debounce } from 'lodash'
import ReactSelectWrapper from './ReactSelectWrapper'

export interface IAsyncSelectProps {
	type?: string
	resourceProvider: any // this can be either an Object with values or an Promise
	resourceKey: string
	resourceAttribute: string
	resourceParams?: any
	resourceRenderRelationAttribute?: any
	resourceRenderFn?: Function
	resourceInitId?: any
	debounceTime?: number
	debounceMinimumCharacters?: number
	onBlur?: Function
	onChange?: Function
	value?: any
	defaultOptions?: any
	defaultValue?: any
	valueInitFn?: Function
	valueSelectIfOne?: boolean
	filterOptions?: Function
	formatOptionLabel?: Function
	[key: string]: any
}

const AsyncResourceSelect = (props: IAsyncSelectProps) => {
	const {
		type = 'AsyncSelect',
		resourceProvider,
		resourceKey,
		resourceAttribute,
		resourceRenderFn,
		resourceRenderRelationAttribute,
		resourceParams,
		resourceInitId = false,
		defaultOptions = true,
		debounceTime = 100,
		debounceMinimumCharacters = 0,
		onBlur,
		onChange,
		valueInitFn,
		valueSelectIfOne = false,
		filterOptions,
		formatOptionLabel,
		...rest
	} = props

	const [, forceUpdate] = useReducer((x) => x + 1, 0)
	const [noOptionsMessage, setNoOptionsMessage] = useState('No options')
	const [defaultValue, setDefaultValue] = useState(null)
	const [res, setRes] = useState(null)

	const loadOptions = (inputValue, callback) => {
		if (
			debounceMinimumCharacters &&
			inputValue.length < debounceMinimumCharacters
		) {
			setNoOptionsMessage(
				'Type minimaal ' + debounceMinimumCharacters + ' characters'
			)
			return callback(false)
		} else {
			setNoOptionsMessage('No Options')
		}

		if (typeof resourceProvider == 'object') {
			callback(resourceProvider)
			return
		}
		let params = { ...defaultParams, ...resourceParams }
		params.filter.q = inputValue

		let resource = resourceProvider(params)
		if (resource instanceof Promise) {
			resource.then((res) => {
				setRes(res)
				let options = mapResultKeyToArray(res, resourceKey)

				if (filterOptions) {
					options = filterOptions(options)
				}

				if (resourceInitId) {
					let setOption = options.filter((obj) => {
						return obj.id === resourceInitId
					})

					setValue(setOption[0])
					setDefaultValue(setOption[0])
				}

				if (options.length == 1 && valueSelectIfOne) {
					setValue(options[0])
					setDefaultValue(options[0])
				}

				callback(options)
				if (valueInitFn) {
					valueInitFn(options, setValue)
				}
			})
		} else {
			callback(resource)
		}
	}

	const setValue = (val) => {
		forceUpdate()
		onChange(val)
	}

	const doFormatOptionLabel = (option, { context }) => {
		// custom function
		if (formatOptionLabel) {
			return formatOptionLabel(option, context)
		}

		// render a relation field small
		if (
			resourceRenderRelationAttribute &&
			res[resourceRenderRelationAttribute.relation] != undefined &&
			context == 'menu'
		) {
			let relationId =
				option?.relationships[resourceRenderRelationAttribute.relation]?.data
					?.id
			return (
				<>
					<div>{option.attributes && option.attributes[resourceAttribute]}</div>
					<div className="text-xs text-gray-400">
						{
							res[resourceRenderRelationAttribute.relation][relationId]
								?.attributes[resourceRenderRelationAttribute.attribute]
						}
					</div>
				</>
			)
		}
		// just render the attribute
		return option.attributes[resourceAttribute]
	}

	return (
		<ReactSelectWrapper
			type={type}
			isClearable={true}
			getOptionLabel={(option) =>
				formatOptionLabel
					? option
					: option.attributes && option.attributes[resourceAttribute]
			}
			noOptionsMessage={() => noOptionsMessage}
			defaultValue={defaultValue}
			getOptionValue={(option) => option}
			defaultOptions={defaultOptions}
			loadOptions={debounce(loadOptions, debounceTime)}
			onBlur={() => onBlur}
			onChange={(val) => setValue(val)}
			formatOptionLabel={doFormatOptionLabel}
			{...rest}
		/>
	)
}

AsyncResourceSelect.isFormable = true

export default AsyncResourceSelect
